import { createValidationSchema, emailField, isError, PosixDate, runUnsafe, stringField, UnexpectedError, } from "@for-it/domain-lib";
import { getTenantDbName, sanitizeSubdomain, TenantEntity, TenantStatus, validateSubdomain, } from "../../entities/tenant.js";
import { InvalidSubdomainError } from "../../errors/invalid-subdomain-error.js";
import { SubdomainAlreadyExistsError } from "../../errors/subdomain-already-exists-error.js";
export const createTenant = {
    name: "createTenant",
    description: "Provisions a new tenant: creates its DB, runs migrations, creates S3 bucket, seeds defaults, and inserts the admin row.",
    type: "command",
    isAuthRequired: true,
    payloadSchema: createValidationSchema({
        subdomain: stringField({
            required: true,
            message: "El subdominio es obligatorio (3-63 caracteres: letras minúsculas, números y guiones)",
        }),
        organizationName: stringField({
            required: true,
            message: "El nombre de la organización es obligatorio",
        }),
        contactEmail: emailField({
            required: true,
            message: "El email de contacto es obligatorio",
        }),
        customDomain: stringField({
            required: false,
            message: "El dominio personalizado es opcional",
        }),
        plan: stringField({
            required: false,
            message: "El plan es opcional",
        }),
    }),
    useCase: async ({ db, crypto, tenantBootstrapService }, { subdomain, organizationName, contactEmail, customDomain, plan, }) => {
        const sanitized = sanitizeSubdomain(subdomain);
        if (!validateSubdomain(sanitized)) {
            return new InvalidSubdomainError(subdomain);
        }
        const existing = await db.findOne(TenantEntity, {
            where: { subdomain: sanitized },
        });
        if (existing)
            return new SubdomainAlreadyExistsError(sanitized);
        const tenantId = await runUnsafe(crypto.generateUUID());
        const dbName = getTenantDbName(tenantId);
        const bucketName = `amia-tenant-${sanitized}`;
        const createDbResult = await tenantBootstrapService.createTenantDatabase(dbName);
        if (isError(createDbResult)) {
            await tenantBootstrapService.rollback({ dbName });
            return createDbResult;
        }
        const migrationsResult = await tenantBootstrapService.runTenantMigrations(dbName);
        if (isError(migrationsResult)) {
            await tenantBootstrapService.rollback({ dbName });
            return migrationsResult;
        }
        const bucketResult = await tenantBootstrapService.createTenantBucket(bucketName);
        if (isError(bucketResult)) {
            await tenantBootstrapService.rollback({ dbName, bucketName });
            return bucketResult;
        }
        // TODO(phase-3): once BrandingEntity exists, swap this port call for a
        // typed insert via the tenant DB's EntityManager.
        const brandingResult = await tenantBootstrapService.seedDefaultBranding(dbName, { organizationName });
        if (isError(brandingResult)) {
            await tenantBootstrapService.rollback({ dbName, bucketName });
            return brandingResult;
        }
        // TODO(phase-4): once EmailConfigEntity exists, swap this port call
        // for a typed insert via the tenant DB's EntityManager.
        const emailResult = await tenantBootstrapService.seedDefaultEmailConfig(dbName, {
            fromName: organizationName,
            replyTo: contactEmail,
        });
        if (isError(emailResult)) {
            await tenantBootstrapService.rollback({ dbName, bucketName });
            return emailResult;
        }
        // Auto-seed the default admin user so the operator can log in
        // immediately after the tenant is provisioned. The temporary password
        // is RANDOM (not derived from the public subdomain) so it cannot be
        // guessed. The "Aa1!" suffix guarantees the `passwordField` rules
        // (8+ chars, upper, lower, digit, special) regardless of the random
        // part. The user is forced to change it on first login via
        // `passwordResetToken` (login emits no session token until then).
        const randomPart = await runUnsafe(crypto.generateRandomToken(16));
        const password = `${randomPart}Aa1!`;
        const hashedPassword = await runUnsafe(crypto.hashPassword(password));
        const passwordResetToken = await runUnsafe(crypto.generateUUID());
        const adminResult = await tenantBootstrapService.seedDefaultAdmin(dbName, {
            email: contactEmail,
            hashedPassword,
            firstName: "Admin",
            lastName: organizationName,
            passwordResetToken,
        });
        if (isError(adminResult)) {
            await tenantBootstrapService.rollback({ dbName, bucketName });
            return adminResult;
        }
        // Seed reference data universal (geografía AR hoy; ver
        // domain/src/reference-data/README.md). Si falla, rollback completo:
        // un tenant sin provincias no puede operar (no se pueden crear
        // applicants ni clients sin localidad).
        const referenceDataResult = await tenantBootstrapService.seedTenantReferenceData(dbName);
        if (isError(referenceDataResult)) {
            await tenantBootstrapService.rollback({ dbName, bucketName });
            return referenceDataResult;
        }
        const now = new PosixDate(Date.now());
        const tenant = {
            id: tenantId,
            subdomain: sanitized,
            customDomain: customDomain ?? null,
            organizationName,
            contactEmail,
            dbName,
            status: TenantStatus.ACTIVE,
            subscriptionStatus: null,
            trialEndsAt: null,
            plan: plan ?? null,
            createdAt: now,
            updatedAt: now,
        };
        // Persisting the admin-side tenant row is the last step. If it fails,
        // the tenant DB + bucket + seeds already exist but nothing references
        // them, so roll the provisioned infrastructure back to avoid orphans.
        try {
            await db.save(TenantEntity, tenant);
        }
        catch {
            await tenantBootstrapService.rollback({ dbName, bucketName });
            return new UnexpectedError("No se pudo registrar el tenant; se revirtió la infraestructura provisionada.");
        }
        return {
            tenant,
            temporaryCredentials: {
                email: contactEmail,
                password,
            },
        };
    },
};
